// components/patient/Dashboard.jsx
import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';

export function Dashboard({ user, onNavigate, prescriptions = [], orders = [] }) {
  const { brand } = useBrand();
  
  const userRxs = prescriptions.filter(rx => rx.userId === user.id);
  const allUserOrders = orders.filter(o => o.userId === user.id);
  const userOrders = allUserOrders.filter(o => {
    if (o.brandKey) return o.brandKey === brand.key;
    if (brand.isB2B) return !!o.poNumber;
    return !o.poNumber;
  });
  
  const pendingRxs = userRxs.filter(rx => rx.status === 'pending_review' || rx.status === 'under_review');
  const verifiedRxs = userRxs.filter(rx => rx.status === 'verified');
  const activeOrders = userOrders.filter(o => o.status === 'processing' || o.status === 'shipped');
  const deliveredOrders = userOrders.filter(o => o.status === 'delivered');
  
  const stats = [
    {
      label: 'Total Prescriptions',
      value: userRxs.length,
      icon: 'clipboard',
      color: brand.c.accent
    },
    {
      label: 'Pending Review',
      value: pendingRxs.length,
      icon: 'clock',
      color: '#F59E0B'
    },
    {
      label: 'In Transit',
      value: activeOrders.length,
      icon: 'truck',
      color: '#7C3AED'
    },
    {
      label: 'Delivered',
      value: deliveredOrders.length,
      icon: 'check',
      color: '#10B981'
    }
  ];
  
  const quickActions = [
    {
      icon: 'upload',
      title: 'Upload Prescription',
      description: 'Submit a new prescription for review',
      action: 'upload',
      color: brand.c.accent
    },
    {
      icon: 'package',
      title: 'Bundle Order',
      description: 'Order multiple prescriptions together',
      action: 'bundle',
      color: '#7C3AED'
    },
    {
      icon: 'dollar',
      title: 'Cost Calculator',
      description: 'See how much you can save',
      action: 'savings',
      color: '#10B981'
    }
  ];
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
            Welcome back, {user.firstName}!
          </h1>
          <p style={{ fontSize: 16, color: '#6B7280' }}>
            Here's an overview of your prescriptions and orders
          </p>
        </div>
        
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
          marginBottom: 48
        }}>
          {stats.map((stat, i) => (
            <Card key={i}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{
                    fontSize: 14,
                    color: '#6B7280',
                    marginBottom: 8,
                    fontWeight: 600
                  }}>
                    {stat.label}
                  </div>
                  <div style={{
                    fontSize: 36,
                    fontWeight: 900,
                    color: stat.color,
                    fontFamily: "'Merriweather', serif"
                  }}>
                    {stat.value}
                  </div>
                </div>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: `${stat.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon name={stat.icon} size={24} color={stat.color} />
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Quick Actions */}
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
          Quick Actions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
          marginBottom: 48
        }}>
          {quickActions.map((action, i) => (
            <Card key={i} hover onClick={() => onNavigate(action.action)}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `${action.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16
              }}>
                <Icon name={action.icon} size={28} color={action.color} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
                {action.title}
              </h3>
              <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                {action.description}
              </p>
            </Card>
          ))}
        </div>
        
        {/* Recent Prescriptions */}
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
          Recent Prescriptions
        </h2>
        {userRxs.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Icon name="clipboard" size={32} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                No Prescriptions Yet
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24 }}>
                Upload your first prescription to get started with {brand.name}
              </p>
              <Button onClick={() => onNavigate('upload')} icon="upload">
                Upload Prescription
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: 16, marginBottom: 48 }}>
            {userRxs.slice(0, 3).map(rx => (
              <Card key={rx.id} hover onClick={() => onNavigate('rx-detail', rx.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: brand.c.navy }}>
                        {rx.medication}
                      </h3>
                      <Badge status={rx.status} />
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                      <strong>Prescriber:</strong> {rx.prescriber}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                      <strong>Quantity:</strong> {rx.quantity}
                    </div>
                    {rx.refills !== undefined && (
                      <div style={{ fontSize: 14, color: '#6B7280' }}>
                        <strong>Refills remaining:</strong> {rx.refills}
                      </div>
                    )}
                  </div>
                  <Icon name="arrowRight" size={20} color="#9CA3AF" />
                </div>
              </Card>
            ))}
            {userRxs.length > 3 && (
              <Button variant="ghost" onClick={() => onNavigate('prescriptions')}>
                View All Prescriptions ({userRxs.length})
              </Button>
            )}
          </div>
        )}
        
        {/* Recent Orders */}
        {userOrders.length > 0 && (
          <>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
              Recent Orders
            </h2>
            <div style={{ display: 'grid', gap: 16 }}>
              {userOrders.slice(0, 3).map(order => (
                <Card key={order.id} hover onClick={() => onNavigate('order-detail', order.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: brand.c.navy }}>
                          Order {order.id}
                        </h3>
                        <Badge status={order.status} />
                      </div>
                      <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                        <strong>Items:</strong> {order.prescriptionIds?.length || 0} prescription(s)
                      </div>
                      <div style={{ fontSize: 14, color: '#6B7280' }}>
                        <strong>Placed:</strong> {new Date(order.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Icon name="arrowRight" size={20} color="#9CA3AF" />
                  </div>
                </Card>
              ))}
              {userOrders.length > 3 && (
                <Button variant="ghost" onClick={() => onNavigate('orders')}>
                  View All Orders ({userOrders.length})
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
